import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ControlchatService } from '../../services/controlchat.service';
import { API_BASE_URL } from '../../services/api-base';
import { ControlchatSocketService } from '../../services/controlchat-socket.service';
import { UserSessionService } from '../../services/user-session.service';
import { Subscription } from 'rxjs';

interface ChatUser {
  id: number;
  name: string;
  email?: string;
  online?: boolean;
  picture_user?: string | null;
  picture_url?: string | null;
  avatar?: string | null;
  photo?: string | null;
  role?: string | null;
}

interface ChatConversation {
  id: number;
  type: 'DIRECT' | 'GROUP';
  last_message_at?: string | null;
  last_activity_at?: string | null;
  last_message_text?: string | null;
  last_message_file_name?: string | null;
  last_message_file_type?: string | null;
  last_message_file_path?: string | null;
  last_message_audio_duration?: number | null;
  group_name?: string | null;
  group_created_by?: number | null;
  members_count?: number | null;
  group_only_admins?: number | null;
  other_user_id?: number | null;
  other_user_name?: string | null;
  other_user_email?: string | null;
  other_user_picture?: string | null;
  self_last_read_at?: string | null;
  other_last_read_at?: string | null;
  has_unread?: number | boolean | null;
  mentioned?: number | boolean | null;
}

interface ChatMessage {
  id: number;
  conversation_id: number;
  sender_id: number;
  sender_name?: string;
  message_text?: string | null;
  file_name?: string | null;
  file_path?: string | null;
  sender_picture?: string | null;
  reply_to_message_id?: number | null;
  reply_message_text?: string | null;
  reply_file_name?: string | null;
  reply_file_path?: string | null;
  reply_sender_name?: string | null;
  reactions?: Array<{ emoji: string; count: number; reacted?: boolean }>;
  audio_duration_seconds?: number | null;
  created_at: string;
  read_count?: number | null;
}

@Component({
  selector: 'app-controlchat-widget',
  templateUrl: './controlchat-widget.component.html',
  styleUrls: ['./controlchat-widget.component.scss']
})
export class ControlchatWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLDivElement>;
  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('messageSearchInput') messageSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('groupForm') groupForm?: ElementRef<HTMLDivElement>;
  @ViewChild('monitoringSection') monitoringSection?: ElementRef<HTMLDivElement>;

  private readonly apiBase = API_BASE_URL;

  isOpen = false;
  loadingUsers = false;
  loadingConversations = false;
  loadingMessages = false;
  loadingKeywords = false;
  loadingMonitoring = false;

  searchTerm = '';
  users: ChatUser[] = [];
  conversations: ChatConversation[] = [];
  messages: ChatMessage[] = [];
  messageSearchTerm = '';
  showMessageSearch = false;
  messageMatchIds: number[] = [];
  activeMatchIndex = 0;
  selectedConversation: ChatConversation | null = null;
  selectedUser: ChatUser | null = null;
  messageText = '';
  replyToMessage: ChatMessage | null = null;
  pendingFile: File | null = null;
  pendingFilePreview: string | null = null;
  imageModalSrc: string | null = null;
  audioPreviewUrl: string | null = null;
  pendingAudioDuration: number | null = null;
  isRecording = false;
  recordingSeconds = 0;
  private mediaRecorder: MediaRecorder | null = null;
  private recordingTimerId: number | null = null;
  private autoSendRecording = false;
  isCreatingGroup = false;
  groupName = '';
  groupMemberQuery = '';
  groupMemberResults: ChatUser[] = [];
  groupMembers: ChatUser[] = [];
  isGroupPanelOpen = false;
  isGroupAddMode = false;
  groupMemberSearch = '';
  groupMemberSearchResults: ChatUser[] = [];
  groupMemberFilter = '';
  groupMembersList: ChatUser[] = [];
  unreadConversationIds = new Set<number>();
  lastIncomingConversationId: number | null = null;
  isHeaderMenuOpen = false;
  headerMenuView: 'root' | 'create-group' | 'keywords' = 'root';
  showGroupSettings = false;
  groupOnlyAdmins = false;
  draggingMessage = false;
  showEmojiPicker = false;
  reactionPickerMessageId: number | null = null;
  private editorSelection: Range | null = null;
  showMentionList = false;
  mentionQuery = '';
  private mentionStartIndex = -1;
  private mentionEndIndex = -1;
  private userNearBottom = true;

  readonly emojiList = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👏', '🔥', '✅', '🎉'];
  soundEnabled = true;
  private audioContext: AudioContext | null = null;
  private audioUnlocked = false;
  private unlockHandler = () => this.unlockAudioContext();
  private notificationAudio: HTMLAudioElement | null = null;

  canView = false;
  canCreate = false;
  canManage = false;
  isRestrictedProfile = false;

  keywords: Array<{ id: number; phrase: string; is_active: number }> = [];
  keywordInput = '';
  monitoringHits: any[] = [];

  private tenantId: number | null = null;
  private tenantName = '';
  userId: number | null = null;
  private userSub: Subscription | null = null;
  private socketReady = false;
  private activeConversationId: number | null = null;
  currentUser: ChatUser | null = null;

  constructor(
    private chatService: ControlchatService,
    private socketService: ControlchatSocketService,
    private userSession: UserSessionService,
    private zone: NgZone,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar,
    private titleService: Title
  ) {}

  ngOnInit() {
    this.loadSoundPreference();
    this.attachAudioUnlockListeners();
    this.userSub = this.userSession.user$.subscribe((user) => {
      if (!user?.tenant_id || !user?.id) {
        this.isOpen = false;
        this.canView = false;
        this.canCreate = false;
        this.canManage = false;
        this.isRestrictedProfile = false;
        this.tenantId = null;
        this.userId = null;
        this.currentUser = null;
        this.users = [];
        this.conversations = [];
        this.messages = [];
        this.selectedConversation = null;
        this.selectedUser = null;
        this.socketService.disconnect();
        this.socketReady = false;
        this.tenantName = '';
        this.updateBrowserTitle();
        return;
      }
      const nextTenantId = Number(user.tenant_id);
      const nextUserId = Number(user.id);
      if (this.tenantId === nextTenantId && this.userId === nextUserId && this.socketReady) {
        return;
      }
      this.tenantId = nextTenantId;
      this.userId = nextUserId;
      const currentName = String(
        user?.['name'] || user?.['user_name'] || user?.['full_name'] || user?.['email'] || ''
      ).trim();
      const currentEmail = String(user?.['email'] || user?.['user_email'] || '').trim();
      const currentAvatar =
        user?.['picture_user'] || user?.['picture_url'] || user?.['avatar'] || user?.['photo'] || null;
      this.currentUser = {
        id: nextUserId,
        name: currentName || currentEmail || 'Usuário',
        email: currentEmail || undefined,
        picture_user: currentAvatar
      };
      this.tenantName = String(user?.tenant_name ?? user?.tenant?.name ?? '').trim();
      const roleName = String(user?.role_name || user?.role?.name || '').toLowerCase();
      const isMaster = Number(user?.['is_master'] || user?.['isMaster'] || 0) === 1;
      const isAdmin = roleName.includes('admin') || roleName.includes('master') || Number(user?.role_id || 0) === 1 || isMaster;
      this.isRestrictedProfile = roleName.includes('analisa') || roleName.includes('analista') || roleName.includes('analyst');
      this.syncPermissions(user['modules'] || [], isAdmin);
      if (this.isRestrictedProfile) {
        this.isHeaderMenuOpen = false;
        this.headerMenuView = 'root';
        this.searchTerm = '';
      }
      this.connectSocket();
      this.loadConversations();
      if (this.isOpen) {
        this.bootstrapChat();
      }
      this.updateBrowserTitle();
    });
  }

  ngOnDestroy() {
    this.userSub?.unsubscribe();
    this.socketService.disconnect();
    this.detachAudioUnlockListeners();
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.bootstrapChat();
      this.unlockAudioContext();
      if (this.selectedConversation?.id) {
        this.userNearBottom = true;
        this.scrollMessagesToBottom(true);
        this.markConversationReadIfActive();
      }
      return;
    }
  }

  closePanel() {
    this.isOpen = false;
    this.isHeaderMenuOpen = false;
    this.headerMenuView = 'root';
    this.showMessageSearch = false;
    this.showEmojiPicker = false;
    this.reactionPickerMessageId = null;
  }

  getUserAvatarUrl(user: ChatUser | null) {
    if (!user) return null;
    const raw = user.picture_user || user.picture_url || user.avatar || user.photo || null;
    return this.getAvatarUrl(raw);
  }

  getConversationAvatarUrl(conv: ChatConversation | null) {
    if (!conv) return null;
    const raw = conv.other_user_picture || null;
    return this.getAvatarUrl(raw);
  }

  getMessageAvatarUrl(message: ChatMessage | null) {
    if (!message) return null;
    return this.getAvatarUrl(message.sender_picture || null);
  }

  private getAvatarUrl(raw?: string | null) {
    if (!raw) return null;
    if (raw.startsWith('http')) {
      return raw;
    }
    if (!raw.startsWith('/')) {
      return `${this.apiBase}/${raw}`;
    }
    return `${this.apiBase}${raw}`;
  }

  private syncPermissions(modules: any[], isAdmin = false) {
    if (isAdmin) {
      this.canView = true;
      this.canCreate = true;
      this.canManage = true;
      return;
    }
    if (!Array.isArray(modules) || !modules.length) {
      this.canView = true;
      this.canCreate = true;
      this.canManage = true;
      return;
    }
    const module = modules.find((item) => item.module_key === 'controlchat');
    this.canView = !!module?.can_view;
    this.canCreate = !!module?.can_create;
    this.canManage = !!module?.can_manage;
  }

  private connectSocket() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    if (this.socketReady) {
      return;
    }
    const socket = this.socketService.connect(this.tenantId, this.userId);
    this.socketReady = true;
    this.socketService.on('presence:sync', (payload: any) => {
      this.zone.run(() => {
        const ids = new Set<number>(payload?.onlineUserIds || []);
        this.users = this.users.map((user) => ({ ...user, online: ids.has(user.id) }));
      });
    });
    this.socketService.on('presence:update', (payload: any) => {
      this.zone.run(() => {
        if (!payload?.userId) {
          return;
        }
        const targetId = Number(payload.userId);
        this.users = this.users.map((user) =>
          user.id === targetId ? { ...user, online: !!payload.online } : user
        );
      });
    });
    this.socketService.on('message:new', (message: ChatMessage) => {
      this.zone.run(() => {
        if (!message || !this.userId) {
          return;
        }
        this.updateConversationOnMessage(message);
        const isOwnMessage = message.sender_id === this.userId;
        const isViewingConversation =
          this.isOpen && this.selectedConversation?.id === message.conversation_id;
        if (isViewingConversation) {
          this.addMessageUnique(message);
          this.scrollMessagesToBottom();
          this.clearConversationUnread(message.conversation_id);
          if (!isOwnMessage) {
            this.markConversationReadIfActive();
          }
          return;
        }
        if (!isOwnMessage) {
          this.markConversationUnread(message.conversation_id);
          if (this.soundEnabled) {
            this.playNotificationSound();
          }
        }
      });
    });
    this.socketService.on('conversation:read', (payload: any) => {
      this.zone.run(() => {
        const conversationId = Number(payload?.conversation_id || 0);
        const readerId = Number(payload?.user_id || 0);
        if (!conversationId || !readerId || readerId === this.userId) {
          return;
        }
        if (this.selectedConversation?.id === conversationId) {
          this.loadMessages();
        }
      });
    });
    this.socketService.on('reaction:update', (payload: any) => {
      this.zone.run(() => {
        const messageId = Number(payload?.message_id || 0);
        if (!messageId) return;
        const reactions = payload?.reactions || [];
        this.messages = this.messages.map((msg) =>
          msg.id === messageId ? { ...msg, reactions } : msg
        );
      });
    });
  }

  private bootstrapChat() {
    if (!this.tenantId || !this.userId) {
      return;
    }
    this.loadUsers();
    this.loadConversations();
    if (this.canManage) {
      this.loadKeywords();
      this.loadMonitoring();
    }
  }

  loadUsers() {
    if (!this.tenantId || !this.userId || !this.canView) return;
    this.loadingUsers = true;
    this.chatService.getUsers(this.tenantId, this.userId, this.searchTerm).subscribe({
      next: (rows) => {
        this.users = rows || [];
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
        this.users = [];
      }
    });
  }

  loadConversations() {
    if (!this.tenantId || !this.userId) return;
    this.loadingConversations = true;
    this.chatService.getConversations(this.tenantId, this.userId).subscribe({
      next: (rows) => {
        this.conversations = (rows || []).map((conv) => ({
          ...conv,
          mentioned: this.isMentionForUser(String(conv.last_message_text || '')) ? 1 : 0
        }));
        if (this.selectedConversation?.id) {
          const updated = this.conversations.find((item) => item.id === this.selectedConversation?.id);
          if (updated) {
            this.selectedConversation = updated;
          }
        }
        this.syncUnreadConversations();
        const ids = this.conversations.map((conv) => conv.id);
        this.socketService.joinConversations(ids);
        this.loadingConversations = false;
      },
      error: () => {
        this.loadingConversations = false;
      }
    });
  }

  selectUser(user: ChatUser) {
    if (!this.tenantId || !this.userId) return;
    this.selectedUser = user;
    this.selectedConversation = null;
    this.messages = [];
    this.messageText = '';
    this.setEditorHtml('');
    this.chatService.createDirectConversation(this.tenantId, this.userId, user.id).subscribe({
      next: (response) => {
        const convId = Number(response?.conversation_id || 0);
        if (convId) {
          this.openConversation({
            id: convId,
            type: 'DIRECT',
            other_user_id: user.id,
            other_user_name: user.name,
            other_user_email: user.email,
            other_user_picture: user.picture_user || user.picture_url || user.avatar || user.photo || null
          });
          this.loadConversations();
        }
      }
    });
  }

  clearSearch() {
    this.searchTerm = '';
    this.loadUsers();
  }

  openConversation(conv: ChatConversation) {
    this.selectedConversation = conv;
    this.activeConversationId = conv.id;
    this.userNearBottom = true;
    this.showGroupSettings = false;
    this.groupOnlyAdmins = !!conv.group_only_admins;
    this.replyToMessage = null;
    this.reactionPickerMessageId = null;
    this.messageText = '';
    this.setEditorHtml('');
    this.messages = [];
    this.clearConversationUnread(conv.id);
    this.selectedUser = null;
    this.isGroupPanelOpen = false;
    this.isGroupAddMode = false;
    this.groupMemberSearch = '';
    this.groupMemberSearchResults = [];
    this.groupMemberFilter = '';
    this.groupMembersList = [];
    this.messageSearchTerm = '';
    this.showMessageSearch = false;
    this.updateMessageMatches();
    this.loadMessages();
    if (conv.type === 'GROUP') {
      this.loadGroupMembers();
    }
    this.focusMessageInput();
  }

  markConversationReadIfActive() {
    if (!this.isOpen || !this.selectedConversation?.id) return;
    this.markConversationRead(this.selectedConversation.id);
  }

  loadMessages() {
    if (!this.tenantId || !this.userId || !this.selectedConversation) return;
    const conversationId = this.selectedConversation.id;
    this.activeConversationId = conversationId;
    this.loadingMessages = true;
    this.chatService.getMessages(this.tenantId, this.userId, conversationId).subscribe({
      next: (rows) => {
        if (!this.selectedConversation || this.selectedConversation.id !== conversationId || this.activeConversationId !== conversationId) {
          return;
        }
        const filtered = (rows || []).filter((msg) => msg?.conversation_id === conversationId);
        this.messages = filtered;
        this.loadingMessages = false;
        this.updateMessageMatches();
        this.scrollMessagesToBottom();
        this.markConversationReadIfActive();
      },
      error: () => {
        if (this.selectedConversation && this.selectedConversation.id === conversationId) {
          this.loadingMessages = false;
        }
      }
    });
  }

  onSearchChange() {
    this.loadUsers();
  }

  toggleGroupPanel() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') return;
    this.isGroupPanelOpen = !this.isGroupPanelOpen;
    if (!this.isGroupPanelOpen) {
      this.isGroupAddMode = false;
      this.groupMemberSearch = '';
      this.groupMemberSearchResults = [];
      this.groupMemberFilter = '';
    } else {
      this.loadGroupMembers();
    }
  }

  toggleGroupAddMode() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') return;
    this.isGroupPanelOpen = true;
    this.isGroupAddMode = !this.isGroupAddMode;
    if (this.isGroupAddMode) {
      this.groupMemberFilter = '';
      this.groupMemberSearch = '';
      this.groupMemberSearchResults = [];
    } else {
      this.groupMemberSearch = '';
      this.groupMemberSearchResults = [];
      this.groupMemberFilter = '';
    }
  }

  onGroupMemberFilterChange() {
    if (!this.isGroupAddMode) {
      return;
    }
    this.groupMemberSearch = this.groupMemberFilter;
    this.onGroupMemberSearchChange();
  }

  private onGroupMemberSearchChange() {
    if (!this.tenantId || !this.userId || !this.selectedConversation) return;
    const query = this.groupMemberSearch.trim();
    if (!query) {
      this.groupMemberSearchResults = [];
      return;
    }
    this.chatService.getUsers(this.tenantId, this.userId, query).subscribe({
      next: (rows) => {
        const results = rows || [];
        const existingIds = new Set(this.groupMembersList.map((member) => member.id));
        this.groupMemberSearchResults = results.filter((user) => user.id !== this.userId && !existingIds.has(user.id));
      },
      error: () => {
        this.groupMemberSearchResults = [];
      }
    });
  }

  addMemberToSelectedGroup(user: ChatUser) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    this.chatService.addGroupMember(this.tenantId, this.userId, this.selectedConversation.id, user.id).subscribe({
      next: () => {
        this.groupMemberSearch = '';
        this.groupMemberSearchResults = [];
        this.loadGroupMembers();
        this.loadConversations();
        this.snackBar.open('Integrante adicionado ao grupo.', 'OK', { duration: 2500 });
      }
    });
  }

  canDeleteSelectedGroup() {
    return this.selectedConversation?.type === 'GROUP' && !!this.userId && this.selectedConversation.group_created_by === this.userId;
  }

  deleteSelectedGroup() {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    const confirmed = window.confirm('Deseja excluir este grupo?');
    if (!confirmed) return;
    this.chatService.deleteGroup(this.tenantId, this.userId, this.selectedConversation.id).subscribe({
      next: () => {
        this.selectedConversation = null;
        this.messages = [];
        this.isGroupPanelOpen = false;
        this.isGroupAddMode = false;
        this.loadConversations();
      }
    });
  }

  private loadGroupMembers() {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    this.chatService.getGroupMembers(this.tenantId, this.userId, this.selectedConversation.id).subscribe({
      next: (rows) => {
        this.groupMembersList = rows || [];
      },
      error: () => {
        this.groupMembersList = [];
      }
    });
  }

  get filteredGroupMembers() {
    const term = this.groupMemberFilter.trim().toLowerCase();
    if (!term) return this.groupMembersList;
    return this.groupMembersList.filter((member) => {
      const name = String(member.name || '').toLowerCase();
      const email = String(member.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }

  isGroupOwner() {
    return (
      this.selectedConversation?.type === 'GROUP' &&
      !!this.userId &&
      this.selectedConversation.group_created_by === this.userId
    );
  }

  private getCurrentGroupRole() {
    if (!this.userId) return null;
    const member = this.groupMembersList.find((item) => item.id === this.userId);
    return String(member?.role || '').toUpperCase() || null;
  }

  isGroupAdminOrOwner() {
    if (this.isGroupOwner()) return true;
    const role = this.getCurrentGroupRole();
    return role === 'ADMIN' || role === 'OWNER';
  }

  get isMessageBlocked() {
    return (
      this.selectedConversation?.type === 'GROUP' &&
      this.groupOnlyAdmins &&
      !this.isGroupAdminOrOwner()
    );
  }

  canToggleMemberRole(member: ChatUser) {
    if (!this.isGroupAdminOrOwner()) return false;
    if (!member || !member.id) return false;
    return member.id !== this.userId && member.role !== 'OWNER';
  }

  canRemoveMember(member: ChatUser) {
    if (!this.isGroupAdminOrOwner()) return false;
    if (!member || !member.id) return false;
    return member.id !== this.userId && member.role !== 'OWNER';
  }

  toggleMemberAdmin(member: ChatUser) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    if (!this.canToggleMemberRole(member)) {
      return;
    }
    const nextRole = member.role === 'ADMIN' ? 'MEMBER' : 'ADMIN';
    this.chatService
      .updateGroupMemberRole(this.tenantId, this.userId, this.selectedConversation.id, member.id, nextRole)
      .subscribe({
        next: () => {
          this.groupMembersList = this.groupMembersList.map((item) =>
            item.id === member.id ? { ...item, role: nextRole } : item
          );
          this.snackBar.open(
            nextRole === 'ADMIN' ? 'Integrante promovido a admin.' : 'Admin removido.',
            'OK',
            { duration: 2500 }
          );
        }
      });
  }

  removeGroupMember(member: ChatUser) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    if (!this.isGroupAdminOrOwner()) {
      return;
    }
    if (member.id === this.userId) {
      return;
    }
    const confirmed = window.confirm('Deseja remover este integrante do grupo?');
    if (!confirmed) return;
    this.chatService
      .removeGroupMember(this.tenantId, this.userId, this.selectedConversation.id, member.id)
      .subscribe({
        next: () => {
          this.groupMembersList = this.groupMembersList.filter((item) => item.id !== member.id);
          this.loadConversations();
          this.snackBar.open('Integrante removido do grupo.', 'OK', { duration: 2500 });
        }
      });
  }

  isMemberAdmin(member: ChatUser) {
    const role = String(member?.role || '').toUpperCase();
    return role === 'ADMIN' || role === 'OWNER';
  }

  toggleGroupForm() {
    if (!this.canCreate) return;
    this.isCreatingGroup = !this.isCreatingGroup;
    if (!this.isCreatingGroup) {
      this.resetGroupForm();
    }
  }

  onGroupMemberSearch() {
    if (!this.tenantId || !this.userId) return;
    const query = this.groupMemberQuery.trim();
    if (!query) {
      this.groupMemberResults = [];
      return;
    }
    this.chatService.getUsers(this.tenantId, this.userId, query).subscribe({
      next: (rows) => {
        const results = rows || [];
        const selectedIds = new Set(this.groupMembers.map((member) => member.id));
        this.groupMemberResults = results.filter((user) => !selectedIds.has(user.id));
      },
      error: () => {
        this.groupMemberResults = [];
      }
    });
  }

  addGroupMember(user: ChatUser) {
    const exists = this.groupMembers.some((member) => member.id === user.id);
    if (exists) return;
    this.groupMembers = [...this.groupMembers, user];
    this.groupMemberQuery = '';
    this.groupMemberResults = [];
  }

  removeDraftGroupMember(member: ChatUser) {
    this.groupMembers = this.groupMembers.filter((item) => item.id !== member.id);
  }

  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmojiToMessage(emoji: string) {
    if (!this.messageInput?.nativeElement) {
      this.messageText = `${this.messageText || ''}${emoji}`;
      this.showEmojiPicker = false;
      return;
    }
    this.insertTextAtCursor(emoji);
    this.showEmojiPicker = false;
  }

  closeEmojiPicker() {
    this.showEmojiPicker = false;
  }

  preserveEditorSelection(event: Event) {
    this.saveEditorSelection();
  }

  saveEditorSelection() {
    const editor = this.messageInput?.nativeElement;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    this.editorSelection = range;
  }

  private restoreEditorSelection() {
    const selection = window.getSelection();
    if (!selection || !this.editorSelection) return;
    selection.removeAllRanges();
    selection.addRange(this.editorSelection);
  }

  onEditorInput() {
    const html = this.getEditorHtml();
    this.messageText = html;
    if (this.isEditorEmpty(html)) {
      this.setEditorHtml('');
    }
    this.saveEditorSelection();
  }

  applyFormat(command: string) {
    this.restoreEditorSelection();
    document.execCommand(command, false);
    this.onEditorInput();
  }

  applyColor(command: 'foreColor' | 'hiliteColor', event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input?.value) return;
    this.restoreEditorSelection();
    document.execCommand(command, false, input.value);
    this.onEditorInput();
  }

  private insertTextAtCursor(text: string) {
    const editor = this.messageInput?.nativeElement;
    if (!editor) return;
    editor.focus();
    this.restoreEditorSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      editor.appendChild(document.createTextNode(text));
      this.onEditorInput();
      return;
    }
    const range = selection.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    this.saveEditorSelection();
    this.onEditorInput();
  }

  private getEditorHtml() {
    return this.messageInput?.nativeElement?.innerHTML || this.messageText || '';
  }

  private setEditorHtml(html: string) {
    const editor = this.messageInput?.nativeElement;
    if (editor) {
      editor.innerHTML = html;
    }
    this.messageText = html;
  }

  private isEditorEmpty(html: string) {
    const text = this.extractPlainText(html);
    return !text.trim();
  }

  private placeCaretAtEnd(element: HTMLElement) {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
  }

  setReplyTo(message: ChatMessage) {
    this.replyToMessage = message;
    this.focusMessageInput();
  }

  clearReply() {
    this.replyToMessage = null;
  }

  toggleReactionPicker(message: ChatMessage) {
    if (!message?.id) return;
    this.reactionPickerMessageId = this.reactionPickerMessageId === message.id ? null : message.id;
  }

  addReaction(message: ChatMessage, emoji: string) {
    if (!this.tenantId || !this.userId || !message?.id) return;
    this.chatService.toggleReaction(this.tenantId, this.userId, message.id, emoji).subscribe({
      next: (payload) => {
        const nextReactions = payload?.reactions || [];
        this.messages = this.messages.map((msg) => (msg.id === message.id ? { ...msg, reactions: nextReactions } : msg));
        this.reactionPickerMessageId = null;
      }
    });
  }

  trackMessage(index: number, msg: ChatMessage) {
    return msg.id;
  }

  onMessagesScroll() {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return;
    const threshold = 80;
    this.userNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }

  handleMessageKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') return;
    if (event.shiftKey) return;
    event.preventDefault();
    this.sendMessage();
  }

  get filteredMentionMembers() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return [];
    }
    const term = this.mentionQuery.trim().toLowerCase();
    return this.groupMembersList
      .filter((member) => member.id !== this.userId)
      .filter((member) => {
        if (!term) return true;
        const name = String(member.name || '').toLowerCase();
        const email = String(member.email || '').toLowerCase();
        return name.includes(term) || email.includes(term);
      })
      .slice(0, 8);
  }

  selectMentionMember(member: ChatUser) {
    if (!member) return;
    this.insertMentionToken(`@${this.formatUserName(member.name)}`, { id: member.id });
  }

  selectMentionAll() {
    this.insertMentionToken('@Todos', { all: true });
  }

  private updateMentionState() {
    if (!this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      this.closeMentionList();
      return;
    }
    const editor = this.messageInput?.nativeElement;
    const selection = window.getSelection();
    if (!editor || !selection || selection.rangeCount === 0) {
      this.closeMentionList();
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.endContainer)) {
      this.closeMentionList();
      return;
    }
    const preRange = range.cloneRange();
    preRange.selectNodeContents(editor);
    preRange.setEnd(range.endContainer, range.endOffset);
    const text = preRange.toString();
    const atIndex = text.lastIndexOf('@');
    if (atIndex === -1) {
      this.closeMentionList();
      return;
    }
    if (atIndex > 0 && !/\s/.test(text[atIndex - 1])) {
      this.closeMentionList();
      return;
    }
    const query = text.slice(atIndex + 1);
    if (!/^[^\s@]*$/.test(query)) {
      this.closeMentionList();
      return;
    }
    this.mentionQuery = query;
    this.mentionStartIndex = atIndex;
    this.mentionEndIndex = text.length;
    this.showMentionList = true;
  }

  private closeMentionList() {
    this.showMentionList = false;
    this.mentionQuery = '';
    this.mentionStartIndex = -1;
    this.mentionEndIndex = -1;
  }

  private insertMentionToken(label: string, options: { id?: number; all?: boolean }) {
    const editor = this.messageInput?.nativeElement;
    if (!editor || this.mentionStartIndex < 0 || this.mentionEndIndex < 0) {
      return;
    }
    editor.focus();
    const range = this.buildRangeFromTextIndexes(editor, this.mentionStartIndex, this.mentionEndIndex);
    if (!range) {
      return;
    }
    range.deleteContents();
    const span = document.createElement('span');
    span.className = 'mention-tagged';
    span.textContent = label;
    if (options.all) {
      span.setAttribute('data-mention', 'all');
    }
    if (options.id) {
      span.setAttribute('data-mention-id', String(options.id));
    }
    span.setAttribute('contenteditable', 'false');
    const space = document.createTextNode(' ');
    range.insertNode(space);
    range.insertNode(span);
    const selection = window.getSelection();
    if (!selection) {
      this.closeMentionList();
      return;
    }
    const nextRange = document.createRange();
    nextRange.setStartAfter(space);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    this.saveEditorSelection();
    this.closeMentionList();
    this.onEditorInput();
  }

  private buildRangeFromTextIndexes(root: HTMLElement, startIndex: number, endIndex: number) {
    const start = this.findTextPosition(root, startIndex);
    const end = this.findTextPosition(root, endIndex);
    if (!start || !end) return null;
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  }

  private findTextPosition(root: HTMLElement, index: number) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let currentIndex = 0;
    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      const length = node.nodeValue?.length || 0;
      if (currentIndex + length >= index) {
        return { node, offset: index - currentIndex };
      }
      currentIndex += length;
    }
    const fallbackNode = root.lastChild;
    if (fallbackNode && fallbackNode.nodeType === Node.TEXT_NODE) {
      const text = fallbackNode as Text;
      return { node: text, offset: text.nodeValue?.length || 0 };
    }
    return null;
  }

  isUserOnline(userId?: number | null) {
    if (!userId) return false;
    return !!this.users.find((user) => user.id === userId)?.online;
  }

  private focusMessageInput() {
    setTimeout(() => {
      const editor = this.messageInput?.nativeElement;
      if (!editor) return;
      editor.focus();
      this.placeCaretAtEnd(editor);
    }, 0);
  }

  formatUserName(name?: string | null) {
    if (!name) return '';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 2) {
      return parts.join(' ');
    }
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

  handleFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input?.files?.length) return;
    this.setPendingFile(input.files[0]);
    if (input) {
      input.value = '';
    }
  }

  onMessageDragOver(event: DragEvent) {
    event.preventDefault();
    if (this.isMessageBlocked) return;
    this.draggingMessage = true;
  }

  onMessageDragLeave(event: DragEvent) {
    event.preventDefault();
    const related = event.relatedTarget as HTMLElement | null;
    if (related && this.messageInput?.nativeElement?.contains(related)) {
      return;
    }
    this.draggingMessage = false;
  }

  onMessageDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingMessage = false;
    if (this.isMessageBlocked) return;
    const file = event.dataTransfer?.files?.[0] || null;
    if (!file) return;
    this.setPendingFile(file);
  }

  clearFile() {
    this.clearPendingPreview();
    this.pendingFile = null;
    this.clearAudioPreview();
    this.pendingAudioDuration = null;
  }

  private setPendingFile(file: File | null) {
    if (!file) return;
    this.clearPendingPreview();
    this.clearAudioPreview();
    this.pendingFile = file;
    this.pendingAudioDuration = null;
    if (this.isImageFile(file)) {
      this.pendingFilePreview = URL.createObjectURL(file);
    } else if (this.isAudioFile(file)) {
      this.audioPreviewUrl = URL.createObjectURL(file);
      this.resolveAudioDuration(file).then((duration) => {
        this.pendingAudioDuration = duration;
      });
    } else {
      this.pendingFilePreview = null;
    }
  }

  private clearAudioPreview() {
    if (this.audioPreviewUrl) {
      URL.revokeObjectURL(this.audioPreviewUrl);
      this.audioPreviewUrl = null;
    }
  }

  private async resolveAudioDuration(file: File): Promise<number | null> {
    try {
      const url = URL.createObjectURL(file);
      const audio = new Audio();
      audio.src = url;
      const duration = await new Promise<number>((resolve, reject) => {
        audio.addEventListener('loadedmetadata', () => resolve(audio.duration));
        audio.addEventListener('error', () => reject(new Error('invalid audio')));
      });
      URL.revokeObjectURL(url);
      return Number.isFinite(duration) ? Math.round(duration) : null;
    } catch {
      return null;
    }
  }

  sendMessage() {
    if (!this.tenantId || !this.userId || !this.selectedConversation) return;
    if (this.isMessageBlocked) {
      this.snackBar.open('Somente administradores podem enviar mensagens neste grupo.', 'OK', { duration: 3000 });
      return;
    }
    if (this.isRecording) {
      this.autoSendRecording = true;
      this.stopRecording();
      return;
    }
    const rawHtml = this.getEditorHtml();
    const sanitizedHtml = this.sanitizeMessageHtml(rawHtml);
    const plainText = this.extractPlainText(sanitizedHtml);
    if (!plainText.trim() && !this.pendingFile) {
      return;
    }
    const message = sanitizedHtml;
    const replyToId = this.replyToMessage?.id || null;
    const audioDuration = this.pendingAudioDuration ?? null;
    this.chatService
      .sendMessage(
        this.tenantId,
        this.userId,
        this.selectedConversation.id,
        message,
        this.pendingFile,
        replyToId,
        audioDuration
      )
      .subscribe({
        next: (response) => {
          this.messageText = '';
          this.setEditorHtml('');
          this.clearPendingPreview();
          this.pendingFile = null;
          this.clearAudioPreview();
          this.pendingAudioDuration = null;
          this.replyToMessage = null;
          if (response && response.conversation_id === this.selectedConversation?.id) {
            this.addMessageUnique(response);
            this.scrollMessagesToBottom();
          }
          if (response) {
            this.updateConversationOnMessage(response);
          }
          this.loadConversations();
        }
      });
  }

  private scrollMessagesToBottom(force = false) {
    if (!force && !this.userNearBottom) {
      return;
    }
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    }, 0);
  }

  private addMessageUnique(message: ChatMessage) {
    const exists = this.messages.some((item) => item.id === message.id);
    if (exists) return;
    const normalized = { ...message, read_count: Number(message.read_count || 0) };
    this.messages = [...this.messages, normalized];
    this.updateMessageMatches();
  }

  get hasUnreadMessages() {
    return this.unreadConversationIds.size > 0;
  }

  isConversationUnread(conversationId?: number | null) {
    if (!conversationId) return false;
    return this.unreadConversationIds.has(conversationId);
  }

  isMessageSeen(message: ChatMessage) {
    if (!this.userId || !message) return false;
    if (message.sender_id !== this.userId) return false;
    return this.getMessageReadCount(message) > 0;
  }

  getMessageReadCount(message: ChatMessage) {
    return Number(message?.read_count || 0);
  }

  isGroupMessage(message: ChatMessage) {
    const conv = this.conversations.find((item) => item.id === message.conversation_id) || this.selectedConversation;
    return conv?.type === 'GROUP';
  }

  private markConversationUnread(conversationId: number) {
    this.unreadConversationIds.add(conversationId);
    this.lastIncomingConversationId = conversationId;
    this.conversations = this.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, has_unread: 1 } : conv
    );
    this.updateBrowserTitle();
  }

  private clearConversationUnread(conversationId: number) {
    if (this.unreadConversationIds.has(conversationId)) {
      this.unreadConversationIds.delete(conversationId);
    }
    if (this.lastIncomingConversationId === conversationId) {
      this.lastIncomingConversationId = null;
    }
    this.conversations = this.conversations.map((conv) =>
      conv.id === conversationId ? { ...conv, has_unread: 0 } : conv
    );
    this.updateBrowserTitle();
  }

  private syncUnreadConversations() {
    const nextUnread = new Set<number>();
    this.conversations.forEach((conv) => {
      const flag = conv.has_unread;
      if (flag === true || Number(flag || 0) === 1) {
        nextUnread.add(conv.id);
      }
    });
    this.unreadConversationIds = nextUnread;
    if (this.lastIncomingConversationId && !nextUnread.has(this.lastIncomingConversationId)) {
      this.lastIncomingConversationId = null;
    }
    this.updateBrowserTitle();
  }

  private markConversationRead(conversationId: number) {
    if (!this.tenantId || !this.userId || !conversationId) return;
    this.chatService.markConversationRead(this.tenantId, this.userId, conversationId).subscribe({
      next: () => {
        const now = new Date().toISOString();
        this.conversations = this.conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, self_last_read_at: now, has_unread: 0, mentioned: 0 } : conv
        );
      }
    });
  }

  private updateBrowserTitle() {
    const base = this.tenantName ? `CPS - ${this.tenantName}` : 'CPS';
    if (!this.canView) {
      this.titleService.setTitle(base);
      return;
    }
    const count = this.unreadConversationIds.size;
    if (count > 0) {
      this.titleService.setTitle(`(${count}) ${base}`);
      return;
    }
    this.titleService.setTitle(base);
  }

  private updateConversationOnMessage(message: ChatMessage) {
    const idx = this.conversations.findIndex((conv) => conv.id === message.conversation_id);
    if (idx === -1) {
      this.loadConversations();
      return;
    }
    const existing = this.conversations[idx];
    const mentioned = this.isMentionForUser(message.message_text || '');
    const updated: ChatConversation = {
      ...existing,
      last_message_at: message.created_at || existing.last_message_at || null,
      last_activity_at: message.created_at || existing.last_activity_at || null,
      last_message_text: message.message_text ?? null,
      last_message_file_name: message.file_name ?? null,
      last_message_file_path: message.file_path ?? null,
      mentioned: mentioned ? 1 : 0
    };
    this.conversations = [
      updated,
      ...this.conversations.slice(0, idx),
      ...this.conversations.slice(idx + 1)
    ];
  }

  private isMentionForUser(messageText: string) {
    if (!this.userId) return false;
    const text = String(messageText || '');
    if (text.includes('data-mention="all"')) {
      return true;
    }
    return text.includes(`data-mention-id="${this.userId}"`);
  }

  isConversationMentioned(conv: ChatConversation) {
    const mentioned = conv.mentioned === true || Number(conv.mentioned || 0) === 1;
    return mentioned;
  }

  get filteredMessages() {
    return this.messages;
  }

  toggleMessageSearch() {
    this.showMessageSearch = !this.showMessageSearch;
    if (!this.showMessageSearch) {
      this.messageSearchTerm = '';
      this.updateMessageMatches();
      return;
    }
    setTimeout(() => this.messageSearchInput?.nativeElement.focus(), 0);
  }

  onMessageSearchChange() {
    this.updateMessageMatches(true);
  }

  get messageMatchCount() {
    return this.messageMatchIds.length;
  }

  get messageMatchLabel() {
    const total = this.messageMatchIds.length;
    if (!total) return '0 / 0';
    return `${this.activeMatchIndex + 1} / ${total}`;
  }

  isMessageMatch(msg: ChatMessage) {
    return this.messageMatchIds.includes(msg.id);
  }

  isActiveMessageMatch(msg: ChatMessage) {
    const activeId = this.messageMatchIds[this.activeMatchIndex];
    return activeId === msg.id;
  }

  goToNextMatch() {
    const total = this.messageMatchIds.length;
    if (!total) return;
    this.activeMatchIndex = (this.activeMatchIndex + 1) % total;
    this.scrollToMessage(this.messageMatchIds[this.activeMatchIndex]);
  }

  goToPrevMatch() {
    const total = this.messageMatchIds.length;
    if (!total) return;
    this.activeMatchIndex = (this.activeMatchIndex - 1 + total) % total;
    this.scrollToMessage(this.messageMatchIds[this.activeMatchIndex]);
  }

  private updateMessageMatches(scrollToFirst = false) {
    const term = this.messageSearchTerm.trim().toLowerCase();
    if (!term) {
      this.messageMatchIds = [];
      this.activeMatchIndex = 0;
      return;
    }
    this.messageMatchIds = this.messages
      .filter((msg) => this.isMessageMatchingTerm(msg, term))
      .map((msg) => msg.id);
    if (this.activeMatchIndex >= this.messageMatchIds.length) {
      this.activeMatchIndex = 0;
    }
    if (scrollToFirst && this.messageMatchIds.length) {
      this.scrollToMessage(this.messageMatchIds[this.activeMatchIndex]);
    }
  }

  private isMessageMatchingTerm(msg: ChatMessage, term: string) {
    const text = this.extractPlainText(String(msg.message_text || '')).toLowerCase();
    const sender = String(msg.sender_name || '').toLowerCase();
    const fileName = String(msg.file_name || '').toLowerCase();
    return text.includes(term) || sender.includes(term) || fileName.includes(term);
  }

  private scrollToMessage(messageId: number) {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement.querySelector(`#msg-${messageId}`) as HTMLElement | null;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  }

  getConversationPreview(conv: ChatConversation) {
    const fileType = String(conv.last_message_file_type || '').toLowerCase();
    if (fileType.startsWith('audio/')) {
      return this.audioPreviewLabel(conv.last_message_audio_duration);
    }
    const text = this.extractPlainText(String(conv.last_message_text || ''));
    if (text) {
      return this.truncatePreview(text);
    }
    if (conv.last_message_file_name) {
      if (this.isAudioFileName(conv.last_message_file_name)) {
        return this.audioPreviewLabel(conv.last_message_audio_duration);
      }
      return this.truncatePreview(`Arquivo: ${conv.last_message_file_name}`);
    }
    if (conv.last_message_file_path) {
      return this.truncatePreview('Arquivo enviado');
    }
    return '';
  }

  toggleHeaderMenu() {
    this.isHeaderMenuOpen = !this.isHeaderMenuOpen;
    if (!this.isHeaderMenuOpen) {
      this.headerMenuView = 'root';
    }
  }

  onSoundToggle(enabled: boolean) {
    this.soundEnabled = !!enabled;
    try {
      localStorage.setItem('controlchat-sound-enabled', this.soundEnabled ? '1' : '0');
    } catch (_) {}
    if (this.soundEnabled) {
      this.unlockAudioContext();
    }
  }

  private loadSoundPreference() {
    try {
      const raw = localStorage.getItem('controlchat-sound-enabled');
      if (raw === null) {
        this.soundEnabled = true;
        return;
      }
      this.soundEnabled = raw === '1';
    } catch (_) {
      this.soundEnabled = true;
    }
  }

  private playNotificationSound() {
    try {
      if (this.notificationAudio) {
        this.notificationAudio.currentTime = 0;
        const playPromise = this.notificationAudio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => undefined);
        }
        return;
      }
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      const ctx = this.audioContext;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => undefined);
      }
      if (!this.audioUnlocked) {
        return;
      }
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.26);
    } catch (_) {
      // ignore audio errors
    }
  }

  private unlockAudioContext() {
    try {
      if (!this.notificationAudio) {
        this.notificationAudio = new Audio('/assets/notify.wav');
        this.notificationAudio.volume = 0.6;
      }
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => undefined);
      }
      this.audioUnlocked = true;
      this.detachAudioUnlockListeners();
    } catch (_) {
      // ignore
    }
  }

  private attachAudioUnlockListeners() {
    const events = ['click', 'keydown', 'touchstart'];
    events.forEach((evt) => window.addEventListener(evt, this.unlockHandler, { passive: true }));
  }

  private detachAudioUnlockListeners() {
    const events = ['click', 'keydown', 'touchstart'];
    events.forEach((evt) => window.removeEventListener(evt, this.unlockHandler));
  }

  openCreateGroupFromMenu() {
    this.headerMenuView = 'create-group';
    this.isCreatingGroup = true;
    this.scrollToGroupForm();
  }

  openKeywordsFromMenu() {
    this.headerMenuView = 'keywords';
    this.isCreatingGroup = false;
    this.scrollToMonitoring();
  }

  closeHeaderMenu() {
    this.isHeaderMenuOpen = false;
    this.headerMenuView = 'root';
  }

  backHeaderMenu() {
    this.headerMenuView = 'root';
  }

  private scrollToGroupForm() {
    setTimeout(() => this.groupForm?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }

  private scrollToMonitoring() {
    setTimeout(() => this.monitoringSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }

  formatConversationTime(value?: string | null) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((startToday.getTime() - startDate.getTime()) / 86400000);
    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) {
      return 'Ontem';
    }
    if (diffDays > 1 && diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'long' });
    }
    return date.toLocaleDateString('pt-BR');
  }

  private truncatePreview(value: string, maxWords = 8, maxChars = 72) {
    const normalized = String(value || '').replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return '';
    }
    let result = normalized;
    let truncated = false;
    const words = normalized.split(' ');
    if (words.length > maxWords) {
      result = words.slice(0, maxWords).join(' ');
      truncated = true;
    }
    if (result.length > maxChars) {
      const slice = result.slice(0, maxChars).trimEnd();
      const lastSpace = slice.lastIndexOf(' ');
      result = lastSpace > Math.floor(maxChars * 0.6) ? slice.slice(0, lastSpace) : slice;
      truncated = true;
    }
    return truncated ? `${result} (..)` : result;
  }

  getHighlightedMessage(msg: ChatMessage): SafeHtml {
    const rawHtml = String(msg.message_text || '');
    const term = this.messageSearchTerm.trim();
    const sanitized = this.sanitizeMessageHtml(rawHtml);
    if (!term) {
      return this.sanitizer.bypassSecurityTrustHtml(sanitized);
    }
    const highlighted = this.highlightHtmlText(sanitized, term);
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  getReplyPreviewHtml(msg: ChatMessage): SafeHtml {
    if (!msg) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    if (msg.reply_message_text) {
      const sanitized = this.sanitizeMessageHtml(String(msg.reply_message_text || ''));
      return this.sanitizer.bypassSecurityTrustHtml(sanitized);
    }
    const fallback = msg.reply_file_name || 'Arquivo';
    return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(String(fallback)));
  }

  private escapeHtml(value: string) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private sanitizeMessageHtml(html: string) {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild as HTMLElement | null;
    if (!root) return '';
    const allowedTags = new Set([
      'B',
      'STRONG',
      'I',
      'EM',
      'U',
      'S',
      'BR',
      'UL',
      'OL',
      'LI',
      'SPAN',
      'P',
      'DIV'
    ]);
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    const toProcess: Element[] = [];
    while (walker.nextNode()) {
      toProcess.push(walker.currentNode as Element);
    }
    toProcess.forEach((el) => {
      const element = el as HTMLElement;
      if (!allowedTags.has(element.tagName)) {
        const parent = el.parentNode;
        if (!parent) return;
        while (el.firstChild) {
          parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
        return;
      }
      const style = element.getAttribute('style');
      const allowedAttrs = new Set(['style']);
      if (element.tagName === 'SPAN' && element.classList.contains('mention-tagged')) {
        allowedAttrs.add('class');
        allowedAttrs.add('data-mention-id');
        allowedAttrs.add('data-mention');
      }
      Array.from(element.attributes).forEach((attr) => {
        if (!allowedAttrs.has(attr.name)) {
          element.removeAttribute(attr.name);
        }
      });
      if (style) {
        const nextStyles: string[] = [];
        const color = element.style.color;
        const background = element.style.backgroundColor;
        if (color) {
          nextStyles.push(`color: ${color}`);
        }
        if (background) {
          nextStyles.push(`background-color: ${background}`);
        }
        if (nextStyles.length) {
          element.setAttribute('style', nextStyles.join('; '));
        } else {
          element.removeAttribute('style');
        }
      }
    });
    return root.innerHTML;
  }

  private extractPlainText(html: string) {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return '';
    return (root.textContent || '').replace(/\u00a0/g, ' ');
  }

  private highlightHtmlText(html: string, term: string) {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild as HTMLElement | null;
    if (!root) return html;
    const regex = new RegExp(this.escapeRegExp(term), 'gi');
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    while (walker.nextNode()) {
      nodes.push(walker.currentNode as Text);
    }
    nodes.forEach((node) => {
      const text = node.nodeValue || '';
      regex.lastIndex = 0;
      if (!regex.test(text)) {
        return;
      }
      regex.lastIndex = 0;
      const frag = doc.createDocumentFragment();
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text))) {
        if (match.index > lastIndex) {
          frag.append(text.slice(lastIndex, match.index));
        }
        const span = doc.createElement('span');
        span.className = 'match-highlight';
        span.textContent = match[0];
        frag.append(span);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        frag.append(text.slice(lastIndex));
      }
      node.parentNode?.replaceChild(frag, node);
    });
    return root.innerHTML;
  }

  private escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  fileExtLabel(name?: string | null) {
    const ext = this.extractFileExt(name);
    if (!ext) return 'ARQ';
    return ext.toUpperCase();
  }

  truncateFileName(name: string, max = 22) {
    const clean = String(name || '').trim();
    if (!clean) return '';
    if (clean.length <= max) return clean;
    return `${clean.slice(0, max - 3)}...`;
  }

  private extractFileExt(name?: string | null) {
    if (!name) return '';
    const clean = name.trim();
    const parts = clean.split('.');
    if (parts.length < 2) return '';
    return parts[parts.length - 1];
  }

  isImageFile(file?: File | null) {
    if (!file) return false;
    return file.type?.startsWith('image/');
  }

  isImageUrl(url?: string | null) {
    if (!url) return false;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.split('?')[0]);
  }

  isAudioFile(file?: File | null) {
    if (!file) return false;
    return file.type?.startsWith('audio/');
  }

  isAudioUrl(url?: string | null) {
    if (!url) return false;
    return /\.(mp3|wav|ogg|webm|m4a|aac)$/i.test(url.split('?')[0]);
  }

  async startRecording() {
    if (this.isRecording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      this.snackBar.open('Gravação de áudio não suportada neste navegador.', 'OK', { duration: 3000 });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const options: MediaRecorderOptions = {};
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        options.mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        options.mimeType = 'audio/ogg';
      }
      const recorder = new MediaRecorder(stream, options);
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size) {
          chunks.push(event.data);
        }
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        const ext = recorder.mimeType?.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type });
        this.setPendingFile(file);
        stream.getTracks().forEach((track) => track.stop());
        if (this.autoSendRecording) {
          this.autoSendRecording = false;
          this.resolveAudioDuration(file).then((duration) => {
            this.pendingAudioDuration = duration;
            this.sendMessage();
          });
        }
      };
      recorder.start();
      this.mediaRecorder = recorder;
      this.isRecording = true;
      this.recordingSeconds = 0;
      this.recordingTimerId = window.setInterval(() => {
        this.recordingSeconds += 1;
      }, 1000);
    } catch (error) {
      this.snackBar.open('Não foi possível acessar o microfone.', 'OK', { duration: 3000 });
    }
  }

  stopRecording() {
    if (!this.mediaRecorder || !this.isRecording) return;
    this.mediaRecorder.stop();
    this.mediaRecorder = null;
    this.isRecording = false;
    if (this.recordingTimerId) {
      window.clearInterval(this.recordingTimerId);
      this.recordingTimerId = null;
    }
  }

  cancelRecording() {
    if (!this.mediaRecorder || !this.isRecording) return;
    this.autoSendRecording = false;
    this.mediaRecorder.stop();
    this.mediaRecorder = null;
    this.isRecording = false;
    if (this.recordingTimerId) {
      window.clearInterval(this.recordingTimerId);
      this.recordingTimerId = null;
    }
    this.recordingSeconds = 0;
  }

  formatRecordingTime(seconds: number) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private isAudioFileName(name?: string | null) {
    if (!name) return false;
    return /\.(mp3|wav|ogg|webm|m4a|aac)$/i.test(name);
  }

  private audioPreviewLabel(duration?: number | null) {
    if (!duration) return 'Áudio';
    return `Áudio · ${this.formatRecordingTime(duration)}`;
  }

  openImageModal(src?: string | null) {
    if (!src) return;
    this.imageModalSrc = src;
  }

  closeImageModal() {
    this.imageModalSrc = null;
  }

  private clearPendingPreview() {
    if (this.pendingFilePreview) {
      URL.revokeObjectURL(this.pendingFilePreview);
      this.pendingFilePreview = null;
    }
  }

  submitGroup() {
    if (!this.tenantId || !this.userId || !this.canCreate) return;
    const name = this.groupName.trim();
    if (!name || this.groupMembers.length === 0) return;
    const members = this.groupMembers.map((member) => member.id);
    this.chatService.createGroupConversation(this.tenantId, this.userId, name, members).subscribe({
      next: (response) => {
        const convId = Number(response?.conversation_id || 0);
        if (convId && !this.conversations.some((conv) => conv.id === convId)) {
          const now = new Date().toISOString();
          const tempConversation: ChatConversation = {
            id: convId,
            type: 'GROUP',
            group_name: name,
            group_created_by: this.userId || null,
            members_count: members.length + 1,
            last_activity_at: now,
            last_message_at: null,
            last_message_text: null,
            last_message_file_name: null,
            last_message_file_path: null,
            group_only_admins: 0
          };
          this.conversations = [tempConversation, ...this.conversations];
        }
        this.loadConversations();
        this.isCreatingGroup = false;
        this.resetGroupForm();
        this.snackBar.open('Grupo criado com sucesso.', 'OK', { duration: 2500 });
        if (this.isHeaderMenuOpen) {
          this.headerMenuView = 'root';
        }
      }
    });
  }

  toggleGroupSettings() {
    this.showGroupSettings = !this.showGroupSettings;
  }

  onGroupOnlyAdminsChange(checked: boolean) {
    if (!this.tenantId || !this.userId || !this.selectedConversation || this.selectedConversation.type !== 'GROUP') {
      return;
    }
    if (!this.isGroupAdminOrOwner()) {
      this.groupOnlyAdmins = !!this.selectedConversation.group_only_admins;
      return;
    }
    this.chatService
      .updateGroupSettings(this.tenantId, this.userId, this.selectedConversation.id, { only_admins: checked ? 1 : 0 })
      .subscribe({
        next: () => {
          this.groupOnlyAdmins = checked;
          this.selectedConversation = {
            ...this.selectedConversation!,
            group_only_admins: checked ? 1 : 0
          };
          this.loadConversations();
        },
        error: () => {
          this.groupOnlyAdmins = !!this.selectedConversation?.group_only_admins;
        }
      });
  }

  private resetGroupForm() {
    this.groupName = '';
    this.groupMemberQuery = '';
    this.groupMemberResults = [];
    this.groupMembers = [];
  }

  loadKeywords() {
    if (!this.tenantId || !this.userId) return;
    this.loadingKeywords = true;
    this.chatService.getKeywords(this.tenantId, this.userId).subscribe({
      next: (rows) => {
        this.keywords = rows || [];
        this.loadingKeywords = false;
      },
      error: () => {
        this.loadingKeywords = false;
      }
    });
  }

  addKeyword() {
    if (!this.keywordInput.trim() || !this.tenantId || !this.userId) return;
    this.chatService.createKeyword(this.tenantId, this.userId, this.keywordInput.trim()).subscribe({
      next: (row) => {
        this.keywords = [row, ...this.keywords];
        this.keywordInput = '';
      }
    });
  }

  removeKeyword(keywordId: number) {
    if (!this.tenantId || !this.userId) return;
    this.chatService.deleteKeyword(this.tenantId, this.userId, keywordId).subscribe({
      next: () => {
        this.keywords = this.keywords.filter((item) => item.id !== keywordId);
      }
    });
  }

  loadMonitoring() {
    if (!this.tenantId || !this.userId) return;
    this.loadingMonitoring = true;
    this.chatService.getMonitoring(this.tenantId, this.userId).subscribe({
      next: (rows) => {
        this.monitoringHits = rows || [];
        this.loadingMonitoring = false;
      },
      error: () => {
        this.loadingMonitoring = false;
      }
    });
  }
}
